import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Gallery from './Gallery';
import { galleryData } from '../data/galleryData';
import { discoverGalleryPhotos, mergeGalleryPhotos } from '../utils/photoDiscovery';
import FamilyTree from './FamilyTree';
import FamilyStats from './FamilyStats';
import { parseGedcomToTree } from '../utils/gedcomParser';
import './FamilyTree.css';
import './EnhancedComponents.css';

const Rossetti = () => {
  const galleryRef = useRef();
  const navigate = useNavigate();
  const [isFamilyTreeOpen, setIsFamilyTreeOpen] = useState(false);
  const [gedcomData, setGedcomData] = useState(null);
  const [showMore, setShowMore] = useState(false);
  const [showCoatOfArms, setShowCoatOfArms] = useState(false);
  const [photos, setPhotos] = useState(galleryData.rossetti || []);

  // Load GEDCOM data for stats
  useEffect(() => {
    const loadGedcomData = async () => {
      try {
        const response = await fetch('/data/rossetti.ged');
        if (response.ok) {
          const content = await response.text();
          const parsed = parseGedcomToTree(content);
          setGedcomData(parsed);
        }
      } catch (error) {
        console.error('Error loading GEDCOM data for stats:', error);
      }
    };
    
    loadGedcomData();
  }, []);

  // Discover additional photos from S3
  useEffect(() => {
    const loadDiscoveredPhotos = async () => {
      try {
        const discoveredPhotos = await discoverGalleryPhotos('rossetti');
        const mergedPhotos = mergeGalleryPhotos(galleryData.rossetti || [], discoveredPhotos);
        setPhotos(mergedPhotos);
      } catch (error) {
        console.warn('Could not load discovered photos:', error);
        setPhotos(galleryData.rossetti || []);
      }
    };

    loadDiscoveredPhotos();
  }, []);

  const handleFamilyTree = () => {
    setIsFamilyTreeOpen(true);
  };

  const handleCloseFamilyTree = () => {
    setIsFamilyTreeOpen(false);
  };



  const handleWeddingGallery = () => {
    navigate('/wedding');
  };

  const handleCelebration = () => {
    setShowMore(!showMore);
  };

  return (
    <section id="rossetti" className="section rossetti-section">
      <div className="section-header">
        <div className="header-content">
          <img src="/images/rossetti.png" alt="Rossetti" className="section-icon" />
          <h1>Rossetti</h1>
          <div className="coat-of-arms-container">
            <img 
              src="/images/rossetti-coat-of-arms.jpg" 
              alt="Rossetti Coat of Arms" 
              className="coat-of-arms"
              onClick={() => setShowCoatOfArms(true)}
              title="Click to view Rossetti Coat of Arms details"
            />
          </div>
        </div>
      </div>
      <p>
	  	Our Rossetti family tree begins with <b>Pasquale Rossetti & Maria Giovannina de Filippo</b> and continues today with their many descendants, some that immigrated to Canada & America, many others staying in Italy.
		The family has remained in touch, across borders, oceans & generations. This site is dedicated in maintaining that family bond for the future generations to come.
      </p>

      {/* Futbol Gallery - Moved to top */}
      <div className="futbol-gallery-section">
        <Gallery 
          ref={galleryRef} 
          images={photos} 
          sectionName="Family Photo Gallery"
        />
      </div>

      {showMore && (
          <div className="extended-intro rossetti-heritage">
            <div className="heritage-content">
              <div className="heritage-text">
                <p>Rossetti is a diminutive form of a truly ancient nickname surname. It derives from the Roman (Latin) "russius", meaning red, and describes essentially light complexioned and fair-haired people. It can be said with confidence that this "nickname" would not originally have been bestowed lightly or as a form of endearment, it was given to the invading red-haired Huns and Goths of the 5th Century A.D. by the dark-skinned Latins.</p>
                <p>The most popular form of the surname is probably Rous(e) or Russell, although the variant forms run into dozens if not hundreds ranging from Le Roux, to Rossey, Rossoni and Rossetti.</p>
                <p>Surnames became necessary when governments introduced personal taxation. In England this was known as Poll Tax. Throughout the centuries, surnames in every country have continued to "develop" often leading to astonishing variants of the original spelling.</p>
                <p>Every European nation has its phalanx of "red" surnames. Probably the earliest known recording in any form is that of Russel of Bury St. Edmunds, Suffolk in 1095, although the first true surname may well be Robert Russel in the Winton Rolls of Hampshire in 1115.</p>
                <p>Early Italian Notables of the Rossetti family include Jacoppi Rossi in 1176; Gherardo Rossi in 1197; and Beringhieri Rossi in 1204. The first French recorded spelling of the family name is shown to be that of Jean Rossin, which was dated September 20th 1639, a christening witness at Tourouvre, Orne, France, during the reign of King Louis XIII of France, 1610 - 1643.</p>
                <p>The Rossetti surname in Italy was first found in the cities of Trento, Udine, Vicenza, Venice, Savigliano, Pisa, Ravenna, Rome, and Castel Campagnano, Province of Caserta. Some of the family migrated even further south into Sicily. This southern branch of the family descended from Count Ugone Rosso of Sicily, whose ancestors were Normans.</p>
              </div>
              <div className="heritage-coat-of-arms">
                <img 
                  src="/images/rossetti-coat-of-arms.jpg" 
                  alt="Rossetti Coat of Arms" 
                  className="heritage-crest"
                />
                <p className="crest-caption">
                  <em>The Rossetti Family Coat of Arms<br/>
                  Symbol of centuries of heritage</em>
                </p>
              </div>
            </div>
          </div>
        )}
		
      <div className="cta-buttons">
        <button className="cta-btn primary" onClick={handleCelebration}>
          <i className="fas fa-shield"></i>
          {showMore ? 'Less' : 'The Rossetti Heritage'}
        </button>

        <button className="cta-btn tertiary" onClick={handleWeddingGallery}>
          <i className="fas fa-heart"></i>
          Wedding Gallery
        </button>
      </div>

	<FamilyStats gedcomData={gedcomData} onViewTree={handleFamilyTree} />

      {isFamilyTreeOpen && (
        <FamilyTree 
          onClose={handleCloseFamilyTree}
        />
      )}

      {/* Coat of Arms Modal */}
      {showCoatOfArms && (
        <div className="coat-of-arms-modal" onClick={() => setShowCoatOfArms(false)}>
          <div className="coat-of-arms-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowCoatOfArms(false)}>
              <i className="fas fa-times"></i>
            </button>
            <div className="coat-of-arms-display">
              <img 
                src="/images/rossetti-coat-of-arms.jpg" 
                alt="Rossetti Coat of Arms" 
                className="coat-of-arms-large"
              />
              <div className="coat-of-arms-info">
                <h2>Rossetti Family Coat of Arms</h2>
                <div className="heraldic-description">
                  <h3>Heraldic Symbolism</h3>
                  <p>
                    The Rossetti coat of arms represents centuries of family heritage and tradition. 
                    The design elements reflect the family's noble origins and their historical significance 
                    in Italian history.
                  </p>
                  <h3>Historical Significance</h3>
                  <p>
                    The Rossetti family name derives from the Roman (Latin) "russius," meaning red, 
                    describing light-complexioned and fair-haired people. The family has deep roots 
                    in Italian nobility, with branches in Trento, Udine, Vicenza, Venice, and notably 
                    in Castel Campagnano, Province of Caserta.
                  </p>
                  <p>
                    Some branches of the family migrated south into Sicily, descending from Count Ugone 
                    Rosso of Sicily, whose ancestors were Normans. The coat of arms serves as a symbol 
                    of this rich heritage and the family's enduring legacy across generations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Rossetti;
